import { useEffect } from 'react';
import useTranslation from 'shared/hooks/useTranslation';
import Trans from 'next-translate/Trans';
import styled, { css } from 'styled-components';
import useModal from 'shared/hooks/useModal';
import useGeoIp from 'shared/hooks/useGeoIp';
import useLanguageSubdomains from 'shared/hooks/useLanguageSubdomains';
import useSubdomain from 'shared/hooks/useSubdomain';
import Button from 'shared/components/Button';

const Text = styled.p`
  color: ${({ theme }) => theme.COLORS.PRIMARY};
  font-family: ${({ theme }) => theme.FONTS.PRIMARY};
  margin: 0;
`;

type GeoIpButtonProps = {
  outlined?: boolean;
};

const GeoIpButton = styled(Button)<GeoIpButtonProps>`
  font-size: inherit;
  padding: 4px 8px;

  ${({ outlined }) =>
    outlined &&
    css`
      background: #ffffff;
      border: 1px solid ${({ theme }) => theme.COLORS.SECONDARY};
      color: ${({ theme }) => theme.COLORS.SECONDARY};
    `}
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const Suggestion = styled.div`
  align-items: center;
  display: flex;
  background: ${({ theme }) => theme.COLORS.ASH};
  flex-direction: column;
  font-size: 10px;
  justify-content: center;
  padding: 4px 16px;
  width: 100%;

  ${({ theme }) => theme.MEDIA.SM} {
    font-size: 12px;
    flex-direction: row;
  }

  ${Text} {
    margin-bottom: 6px;
    margin-right: 0;
    text-align: center;

    ${({ theme }) => theme.MEDIA.SM} {
      margin-bottom: 0;
      margin-right: 16px;
      text-align: left;
    }
  }

  ${GeoIpButton}:first-of-type {
    margin-right: 8px;
  }
`;

type LanguageSuggestionProps = {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  language: string;
};

function LanguageSuggestion({ isOpen, onClose, onAccept, language }: LanguageSuggestionProps): JSX.Element | null {
  const { t } = useTranslation('commonEN');

  return isOpen ? (
    <Suggestion>
      <Text>{t('languageSuggestion.languageDetected')}</Text>
      <ButtonWrapper>
        <GeoIpButton onClick={onAccept} type="button">
          <Trans
            i18nKey="commonEN:languageSuggestion.accept"
            values={{ language: t(`languages.${language}`) }}
            components={[<span key="lang" />]}
            fallback="commonEN:languageSuggestion.accept"
          />
        </GeoIpButton>
        <GeoIpButton onClick={onClose} type="button" outlined>
          {t('languageSuggestion.decline')}
        </GeoIpButton>
      </ButtonWrapper>
    </Suggestion>
  ) : null;
}

function GeoIp(): JSX.Element | null {
  const { lang } = useTranslation();

  const { getModalProps, openModal } = useModal();
  const languageSubdomains = useLanguageSubdomains();
  const { status, countryCode } = useGeoIp();
  const chooseSubdomain = useSubdomain();

  useEffect(() => {
    if (status === 'resolved') {
      if (countryCode && countryCode !== lang && languageSubdomains.languages.includes(countryCode)) {
        openModal();
      } else if (countryCode === lang) {
        chooseSubdomain(languageSubdomains.subdomains[lang], false);
      }
    }
  }, [lang, chooseSubdomain, countryCode, languageSubdomains, openModal, status]);

  if (!countryCode) {
    return null;
  }

  return (
    <LanguageSuggestion
      {...getModalProps<LanguageSuggestionProps>({
        onClose: () => chooseSubdomain(languageSubdomains.subdomains[lang], false),
        onAccept: () => chooseSubdomain(languageSubdomains.subdomains[countryCode]),
        language: countryCode,
      })}
    />
  );
}

export default GeoIp;
